import { useCallback, useState } from 'react';
import { createTicket, updateTicket } from '../../api/endpoints';
import { toApiError } from '../../api/errors';
import type { Id, TicketListItem } from '../../api/types';
import type { TicketFormMode } from './TicketFormDialog';
import { createBody, editBody, isEmptyEdit, type TicketDraft } from './ticket-form';

/**
 * CREATING AND EDITING A TICKET — PH-57's write half.
 *
 * ⭐ ONE HOOK FOR BOTH, the same division `useSprintEditor` makes: one form, one set of refusals, one
 * "the dialog stays open and keeps what you typed" behaviour. Splitting them would duplicate all
 * three and give the error handling two places to drift.
 *
 * ⛔ THE ERROR IS THE API'S OWN WORDS — `reason` first. For these endpoints that is:
 *   · `the request body or query was not valid`            (400, zod — and see `draftProblem`, which
 *                                                            catches the common cases before we send)
 *   · `unknown field 'status'` and friends                 (400, `.strict()` since PH-33)
 *   · `only an active member of this project may …`        (403 — the one that has confused every
 *                                                            human today, because it presents as a
 *                                                            broken button rather than as "you are
 *                                                            in no project". See PH-56.)
 *   · `that sprint belongs to a different project`         (400)
 *   · `parent not found`                                   (400)
 * Every one of those says something "Could not save the ticket" does not.
 *
 * ⭐ THE DIALOG STAYS OPEN ON FAILURE with every field as typed — closing it would lose the form and
 * the message at the same moment.
 */
export interface TicketEditorState {
  open: TicketFormMode | null;
  busy: boolean;
  /** The API's own explanation of a refusal — something WAS sent. */
  error: string | null;
  /**
   * OUR refusal — nothing was sent.
   *
   * ⛔ A SEPARATE FIELD RATHER THAN A SHARED `message`, because the two lead to different next
   * actions. "The server said no" may succeed on retry; "we did not send this" never will until the
   * form changes. Collapsing them is how the dialog came to announce an API refusal for a request
   * the API never saw.
   */
  problem: string | null;
  /** What happened, once it has succeeded. */
  outcome: string | null;
  create: (options?: { sprintId?: Id | null; parentId?: Id | null }) => void;
  edit: (ticket: TicketListItem) => void;
  cancel: () => void;
  dismissOutcome: () => void;
  submit: (draft: TicketDraft) => void;
}

export function useTicketEditor(
  projectId: Id | null,
  onSaved: (ticket: TicketListItem) => void,
): TicketEditorState {
  const [open, setOpen] = useState<TicketFormMode | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const create = useCallback(
    (options: { sprintId?: Id | null; parentId?: Id | null } = {}) => {
      // ⚠️ Belt and braces rather than a tested path: every button that calls this renders only
      //    inside a loaded project. It is here so the hook has one answer for a null project rather
      //    than depending on its callers for correctness.
      if (!projectId) return;
      setOpen({
        kind: 'create',
        projectId: String(projectId),
        sprintId: options.sprintId ?? null,
        parentId: options.parentId ?? null,
      });
      setError(null);
      setProblem(null);
      setOutcome(null);
    },
    [projectId],
  );

  const edit = useCallback((ticket: TicketListItem) => {
    setOpen({ kind: 'edit', ticket });
    setError(null);
    setProblem(null);
    setOutcome(null);
  }, []);

  const cancel = useCallback(() => {
    // ⛔ Not while a request is in flight — if the dialog is dismissed mid-write a FAILURE has
    //    nowhere to appear and the refusal is silent. Same reasoning as `useSprintEditor.cancel`,
    //    including the honest part: the Cancel button also carries `disabled={busy}`, so this is the
    //    second line rather than the one under test.
    setBusy((inFlight) => {
      if (!inFlight) {
        setOpen(null);
        setError(null);
        setProblem(null);
      }
      return inFlight;
    });
  }, []);

  const submit = useCallback(
    (draft: TicketDraft) => {
      const mode = open;
      if (!mode || busy) return;

      setBusy(true);
      setError(null);
      setProblem(null);

      void (async () => {
        try {
          if (mode.kind === 'create') {
            const ticket = await createTicket(
              createBody(draft, { projectId: mode.projectId, parentId: mode.parentId ?? null }),
            );
            setOpen(null);
            // ⭐ The SERVER's key, not one we guessed. `PH-59` is the only handle a person has for
            //   finding this ticket again, and it is assigned by the API.
            setOutcome(`Created ${ticket.key} — ${ticket.title}`);
            onSaved(ticket);
            return;
          }

          const edited = editBody(mode.ticket, draft);
          /**
           * ⛔ NOTHING CHANGED IS NOT A SAVE, and it is refused here rather than sent. The API would
           *   accept the empty PATCH quite happily; this is about not reporting "Saved" for a
           *   request that wrote nothing — the same "looks like it worked" failure as a silent write.
           *
           * 🔴 IT GOES TO `problem`, NOT `error`, AND THAT WAS WRONG IN THE FIRST BUILD OF THIS FILE.
           *   It set `error`, which the dialog renders under the heading "The API refused this
           *   ticket" — a sentence that is FALSE, because nothing was sent to the API at all. Caught
           *   by pressing the button on production, not by any test: every test asserted the message
           *   text and none asserted which heading carried it.
           * ⭐ The distinction is the whole point of keeping two channels: a reader who cannot tell
           *   OUR refusal from the SERVER's cannot tell whether pressing Save again might work.
           *   Having written that comment in the dialog and then violated it one file away is worth
           *   recording — the note did not prevent the mistake, and only using the thing did.
           */
          if (isEmptyEdit(edited)) {
            setProblem('Nothing has changed, so nothing was saved.');
            return;
          }

          const ticket = await updateTicket(mode.ticket.id, edited);
          setOpen(null);
          setOutcome(`Saved ${ticket.key}.`);
          onSaved(ticket);
        } catch (caught) {
          const path = mode.kind === 'create' ? '/tickets' : `/tickets/${mode.ticket.id}`;
          const apiError = toApiError(caught, path);
          setError(apiError.reason ?? apiError.message);
        } finally {
          setBusy(false);
        }
      })();
    },
    [open, busy, onSaved],
  );

  return {
    open,
    busy,
    error,
    problem,
    outcome,
    create,
    edit,
    cancel,
    dismissOutcome: () => setOutcome(null),
    submit,
  };
}
